import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
} from 'axios';
import Cookies from 'universal-cookie';
import { apiRoute } from './constants/api-constants';
import {
  CLIENT_ZONE_HEADER,
  CLOCK_SURFACE_HEADER,
  deviceZone,
  surfaceOfPath,
} from './function-utils/building-time';

// Auth now lives in httpOnly cookies the browser attaches automatically — JS
// can no longer read the token (that's the point: XSS can't steal it). So this
// module does three things instead of bolting on a Bearer header:
//   1. send credentials on every request,
//   2. echo the readable csrf_token cookie as X-CSRF-Token (double-submit),
//   3. transparently refresh an expired access token once, then retry,
//   4. tell the server which zone this DEVICE is in (X-Client-Timezone). The
//      server decides whose clock an answer is on by who is asking (R-T12) and
//      needs the device's zone for an owner or a manager. It travels here,
//      once, instead of as a `tz` parameter remembered at sixteen call sites.
const cookies = new Cookies();

const REFRESH_URL = `${apiRoute}/auth/refresh`;

// Broadcast when the session is truly gone, so AuthProvider can flip to "anon"
// and bounce to the login screen from one place.
export const SESSION_ENDED_EVENT = 'lobbero:session-ended';

// Broadcast when the server answers that the organisation is suspended
// (403 with this code, from requireAuth and from the sign-in route).
export const ORG_SUSPENDED_EVENT = 'lobbero:org-suspended';
export const ORG_SUSPENDED_CODE = 'ORG_SUSPENDED';

// Independent Stays IS8: the account's owner CLOSED it (frozen until reopened
// or removed). requireAuth answers 403 with this code to everyone but the
// owner, and to the owner on every route but the closure and reopen.
export const ACCOUNT_CLOSING_EVENT = 'lobbero:account-closing';
export const ACCOUNT_CLOSING_CODE = 'ACCOUNT_CLOSING';

axios.defaults.withCredentials = true;

type RetriableConfig = AxiosRequestConfig & { _retried?: boolean };

axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const csrf = cookies.get('csrf_token');
  if (csrf && config.headers) {
    config.headers['X-CSRF-Token'] = csrf;
  }

  // Read PER REQUEST, not once at load: a laptop changes zone when it travels.
  // deviceZone() answers '' when the platform will not say, and then nothing is
  // sent — the server falls back by itself and says so (tz_source).
  const zone = deviceZone();
  if (zone && config.headers) {
    config.headers[CLIENT_ZONE_HEADER] = zone;
  }

  // 5. ...and when the reader is standing at the DESK. The desk draws every
  //    time on the building's clock for whoever is in it; this makes the
  //    server cut "today" on that same clock for an owner or a manager, whose
  //    answers are otherwise on their device's. Read per request: the same tab
  //    moves between the desk and the dashboard.
  if (config.headers && surfaceOfPath(window.location.pathname) === 'desk') {
    config.headers[CLOCK_SURFACE_HEADER] = 'desk';
  }

  return config;
});

// Single-flight refresh: a burst of parallel 401s must trigger exactly ONE
// refresh call, and every waiter retries once it settles. Rotating refresh
// tokens make this mandatory — concurrent refreshes would replay an already
// rotated token and trip the backend's reuse detection, killing the session.
let refreshInFlight: Promise<void> | null = null;

const runRefresh = (): Promise<void> => {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(REFRESH_URL, {}, { withCredentials: true })
      .then(() => undefined)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
};

axios.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // The organisation has been suspended: every signed-in request is refused
    // with this code until Lobbero restores it. One announcement, and the app
    // swaps itself for the screen that says so (AuthProvider, App.tsx) instead
    // of each panel failing in its own words.
    const code = (error.response?.data as { code?: string } | undefined)?.code;
    if (status === 403 && code === ORG_SUSPENDED_CODE) {
      window.dispatchEvent(new Event(ORG_SUSPENDED_EVENT));
    }
    if (status === 403 && code === ACCOUNT_CLOSING_CODE) {
      window.dispatchEvent(new Event(ACCOUNT_CLOSING_EVENT));
    }

    // Only a 401 is refreshable. Never try to refresh the refresh call itself,
    // and never loop: one retry per request.
    //
    // AND NEVER REFRESH A SIGN-IN ATTEMPT. A 401 from the login endpoint means
    // "that password is wrong", not "your access token expired" — there is no
    // session to renew. Treating it as expiry fired a pointless POST
    // /auth/refresh on every failed sign-in (observed), and when that refresh
    // also failed it dispatched SESSION_ENDED, so a typo on the login form
    // announced the end of a session that never existed. When a stale refresh
    // cookie DID happen to be valid, it was worse: the refresh succeeded and
    // the interceptor replayed the login POST — the wrong password sent twice.
    const url = config?.url || '';
    const isAuthAttempt =
      url === apiRoute ||
      url.endsWith('/auth/refresh') ||
      url.endsWith('/auth/login') ||
      url.endsWith('/signup');

    if (status !== 401 || !config || config._retried || isAuthAttempt) {
      return Promise.reject(error);
    }

    config._retried = true;

    try {
      await runRefresh();
    } catch {
      // Refresh failed => the session is over (expired, revoked, or reused).
      window.dispatchEvent(new Event(SESSION_ENDED_EVENT));

      return Promise.reject(error);
    }

    return axios(config);
  }
);

export {};
